import { z } from '@qred/components-library';
import {
  validateEmail,
  validatePhoneNumber,
} from '@qred/shared-component-library/src/validators';
import { CountryCode } from '~/enums';
import { requiredString } from '~/helpers/schema.helper';

// Deliberately duplicated (not imported) from StartApplication.schema.ts /
// PersonalInformation.schema.ts. This step exists only behind the
// AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL flag —
// keeping it self-contained means the control flow (and the other six
// markets sharing StartApplication/PersonalInformation) is untouched by
// this experiment, and the whole thing can be deleted in one go once the
// test concludes (or the old code deleted if the variant wins).
const createCompanyDetailsSchema = (market: CountryCode) => {
  const companyNumberSchema =
    market === CountryCode.DE
      ? z.string().optional()
      : z.string().min(1, { message: 'ValidationErrors.Required' });

  return z
    .object({
      companyName: z.string().min(1, { message: 'ValidationErrors.Required' }),
      companyNumber: companyNumberSchema,
      companyType: z.string().optional(),
    })
    .optional();
};

export const createStartApplicationNewSchema = (market: CountryCode) => {
  const companyDetailsSchema = createCompanyDetailsSchema(market);

  return z
    .object({
      companyIsNew: z.boolean(),
      clientId: z.string().optional(),
      companyDetails: companyDetailsSchema,
      phone: requiredString({
        validator: (value) => validatePhoneNumber(value, market),
        message: 'ValidationErrors.Phone',
      }),
      email: requiredString({
        validator: (value) => validateEmail(value),
        message: 'ValidationErrors.Email',
      }),
    })
    .superRefine((data, ctx) => {
      // If neither a client is selected nor it's a new company, error on clientId
      if (!data.clientId && !data.companyIsNew) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ValidationErrors.Required',
          path: ['clientId'],
        });
      }

      // If it's a new company and companyDetails is missing, error on companyDetails
      if (data.companyIsNew && !data.companyDetails) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'ValidationErrors.Required',
          path: ['companyDetails'],
        });
      }
    });
};

export type StartApplicationNewForm = z.infer<
  ReturnType<typeof createStartApplicationNewSchema>
>;
